import clsx from "clsx";
import type { JSX } from "react";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { GripVertical, X } from "lucide-react";
import type {
  AnalyticsBucket,
  AnalyticsFieldKey,
  AnalyticsSource,
  AnalyticsStatusKey,
} from "@adlywise/shared";
import { buildAnalyticsCategories } from "@/features/analytics/filterCategories";
import { DRAWER_TRANSITION_MS } from "./filters/constants";

// Configure drawer for the analytics aggregation report. Emits an AnalyticsConfig
// that drives (a) the aggregation request (source / groupBy / bucket / statuses)
// and (b) the filter drawer's category set. Sections, in order:
//   (a) Type   — source toggle (requests / responses)
//   (b) Time bucket
//   (c) Group-by property picker, GROUPED BY the SAME categories as the filter
//       builder (buildAnalyticsCategories) so the two stay in sync.
// The property picker is ORDERED — selection order IS the GROUP BY / ORDER BY
// order — so selected props render as an ordered, reorderable list.

// Response-side metric statuses. Each selected status becomes its own metric
// group ({total, devices}) in the results table. Wire values are the lowercase
// keys (the shared AnalyticsStatusKey enum); the label is what the UI shows.
// (requests source has no status — its single metric group is a fixed "requests".)
export type MetricStatus = AnalyticsStatusKey;

export const METRIC_STATUS_OPTIONS: { value: MetricStatus; label: string }[] = [
  { value: "bid", label: "Bid" },
  { value: "won", label: "Won" },
  { value: "impression", label: "Impressions" },
  { value: "loss", label: "Loss" },
  { value: "billable", label: "Billable" },
  { value: "nobid", label: "No-bid" },
];

// Default responses selection = the populated statuses.
export const DEFAULT_METRIC_STATUSES: MetricStatus[] = ["bid", "impression"];

export type AnalyticsConfig = {
  source: AnalyticsSource;
  // Ordered: order = GROUP BY order = ORDER BY order. MAY be empty (a pure
  // time series of one metric group per bucket).
  groupBy: AnalyticsFieldKey[];
  bucket: AnalyticsBucket;
  // Ordered response-side metric statuses. Only meaningful when
  // source === "responses"; ignored (fixed "requests" group) on requests.
  statuses: MetricStatus[];
};

export const createDefaultAnalyticsConfig = (): AnalyticsConfig => ({
  source: "requests",
  groupBy: [],
  bucket: "day",
  statuses: DEFAULT_METRIC_STATUSES,
});

// The ordered metric groups a given config produces — the single source of truth
// for the results-table columns and their order. requests → one fixed "requests"
// group; responses → one group per selected status, in selection order.
export type MetricGroup = { key: string; label: string };

export const metricGroupsFor = (config: AnalyticsConfig): MetricGroup[] => {
  if (config.source === "requests") {
    return [{ key: "requests", label: "Requests" }];
  }
  const labelOf = (s: MetricStatus): string =>
    METRIC_STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s;
  return config.statuses.map((s) => ({ key: s, label: labelOf(s) }));
};

// Human labels for each canonical field key. Must stay in sync with the shared
// AnalyticsFieldKeySchema (the enum is the source of truth for the keys). Used
// by AggregatedResultsTable for the group-column headers and as a fallback label
// in the property picker.
export const FIELD_LABELS: Record<AnalyticsFieldKey, string> = {
  country: "Country",
  region: "Region",
  city: "City",
  platform: "Ad source",
  deviceType: "Device type",
  channel: "Channel",
  kind: "Ad format",
  isPaid: "Paid inventory",
  livestream: "Livestream",
  gender: "Co-viewing gender",
  provider: "Provider / Publisher",
  source: "Request source",
  categories: "Content categories",
  campaign: "Campaign",
  segment: "Segment",
  creative: "Creative",
};

const BUCKETS: { value: AnalyticsBucket; label: string }[] = [
  { value: "hour", label: "Hour" },
  { value: "day", label: "Day" },
  { value: "week", label: "Week (Mon–Sun)" },
  { value: "month", label: "Month" },
];

const SOURCES: { value: AnalyticsSource; label: string }[] = [
  { value: "requests", label: "Ad requests" },
  { value: "responses", label: "Ad responses" },
];

const Segmented = <T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (next: T) => void;
}): JSX.Element => (
  <div className="inline-flex rounded-(--radius-md) bg-(--surface-2) p-0.5">
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        onClick={() => onChange(opt.value)}
        className={`rounded-[calc(var(--radius-md)-2px)] px-3 py-1.5 text-[13px] font-medium transition-colors ${
          value === opt.value
            ? "bg-(--surface-1) text-(--text-primary) shadow-sm"
            : "text-(--text-secondary) hover:text-(--text-primary)"
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const CloseIcon = (): JSX.Element => (
  <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 20 20" fill="none">
    <path
      d="M5 5L15 15M15 5L5 15"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const SectionTitle = ({
  title,
  hint,
}: {
  title: string;
  hint?: string;
}): JSX.Element => (
  <span className="text-[12px] font-semibold text-(--text-secondary)">
    {title}
    {hint ? (
      <span className="ml-1.5 font-normal text-(--text-muted)">{hint}</span>
    ) : null}
  </span>
);

export const ConfigureDrawer = ({
  isOpen,
  config,
  onChange,
  onClose,
}: {
  isOpen: boolean;
  config: AnalyticsConfig;
  onChange: (next: AnalyticsConfig) => void;
  onClose: () => void;
}): JSX.Element | null => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(isOpen);

  // Group the selectable properties by the SAME categories the filter builder
  // uses, so the two stay in sync. Campaign props appear only on responses.
  const categories = useMemo(
    () =>
      buildAnalyticsCategories({
        source: config.source,
        includeCampaign: config.source === "responses",
      }),
    [config.source],
  );

  // Fast lookup: field key → its human label (from the category descriptor,
  // which matches the filter builder; FIELD_LABELS as fallback).
  const labelOfField = useMemo(() => {
    const map = new Map<string, string>();
    for (const cat of categories) {
      for (const key of cat.fields) {
        map.set(key, cat.fieldDescriptors[key]?.label ?? FIELD_LABELS[key]);
      }
    }
    return map;
  }, [categories]);

  const fieldLabel = (key: AnalyticsFieldKey): string =>
    labelOfField.get(key) ?? FIELD_LABELS[key];

  useEffect(() => {
    if (isOpen) {
      setIsVisible(false);
      setShouldRender(true);
      return undefined;
    }
    setIsVisible(false);
    if (!shouldRender) return undefined;
    const timeoutId = window.setTimeout(() => {
      setShouldRender(false);
    }, DRAWER_TRANSITION_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (!shouldRender || !isOpen) return undefined;
    let firstFrameId = 0;
    let secondFrameId = 0;
    firstFrameId = window.requestAnimationFrame(() => {
      secondFrameId = window.requestAnimationFrame(() => {
        setIsVisible(true);
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrameId);
      window.cancelAnimationFrame(secondFrameId);
    };
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (!shouldRender) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, shouldRender]);

  const handleSourceChange = (source: AnalyticsSource): void => {
    if (source === config.source) return;
    // Switching source drops any props no longer valid for it (response-side
    // props on requests) so the request stays consistent.
    const nextAllowed = new Set(
      buildAnalyticsCategories({
        source,
        includeCampaign: source === "responses",
      }).flatMap((c) => c.fields),
    );
    onChange({
      ...config,
      source,
      groupBy: config.groupBy.filter((key) => nextAllowed.has(key)),
    });
  };

  const handleBucketChange = (bucket: AnalyticsBucket): void => {
    onChange({ ...config, bucket });
  };

  const toggleStatus = (status: MetricStatus): void => {
    const selected = config.statuses.includes(status);
    if (selected) {
      // At least one status must stay selected.
      if (config.statuses.length === 1) return;
      onChange({
        ...config,
        statuses: config.statuses.filter((s) => s !== status),
      });
      return;
    }
    onChange({ ...config, statuses: [...config.statuses, status] });
  };

  const toggleField = (key: AnalyticsFieldKey): void => {
    if (config.groupBy.includes(key)) {
      onChange({
        ...config,
        groupBy: config.groupBy.filter((k) => k !== key),
      });
      return;
    }
    onChange({ ...config, groupBy: [...config.groupBy, key] });
  };

  const removeField = (key: AnalyticsFieldKey): void => {
    onChange({ ...config, groupBy: config.groupBy.filter((k) => k !== key) });
  };

  const moveField = (index: number, direction: -1 | 1): void => {
    const target = index + direction;
    if (target < 0 || target >= config.groupBy.length) return;
    const next = [...config.groupBy];
    const [moved] = next.splice(index, 1);
    if (moved === undefined) return;
    next.splice(target, 0, moved);
    onChange({ ...config, groupBy: next });
  };

  if (!shouldRender || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className={clsx("fixed inset-0 z-[1400]", {
        "pointer-events-auto": shouldRender,
        "pointer-events-none": !shouldRender,
      })}
    >
      <button
        type="button"
        aria-label="Close configure drawer"
        onClick={onClose}
        className={clsx(
          "absolute inset-0 border-0 bg-[rgba(2,6,23,0.62)] p-0 transition-opacity duration-[220ms] ease-out",
          isVisible ? "opacity-100" : "opacity-0",
        )}
      />

      <aside
        aria-modal="true"
        role="dialog"
        className={clsx(
          "absolute right-0 top-0 flex h-full w-full max-w-[480px] flex-col overflow-hidden bg-(--surface-1) will-change-transform transition-transform duration-[220ms] ease-out",
          isVisible ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex items-start justify-between gap-4 px-5 pt-5">
          <div>
            <h2 className="text-[1.05rem] font-semibold text-(--text-primary)">
              Configure report
            </h2>
            <p className="mt-0.5 text-[0.78rem] text-(--text-muted)">
              Data source, time bucket, and the properties to group by.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close configure"
            className="inline-flex h-8 w-8 items-center justify-center rounded-(--radius-md) border-0 bg-transparent text-(--text-muted) transition hover:bg-(--surface-2) hover:text-(--text-primary)"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-5">
          {/* (a) Type */}
          <div className="flex flex-col gap-2">
            <SectionTitle title="Type" />
            <Segmented
              value={config.source}
              options={SOURCES}
              onChange={handleSourceChange}
            />
          </div>

          {/* Metrics (responses only) */}
          {config.source === "responses" && (
            <div className="flex flex-col gap-2">
              <SectionTitle
                title="Metrics"
                hint="one column group per status (selection order sets column order)"
              />
              <div className="flex flex-wrap gap-2">
                {METRIC_STATUS_OPTIONS.map((opt) => {
                  const order = config.statuses.indexOf(opt.value);
                  const isSelected = order >= 0;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => toggleStatus(opt.value)}
                      aria-pressed={isSelected}
                      className={`inline-flex items-center gap-1.5 rounded-(--radius-pill) border px-3 py-1.5 text-[12px] font-medium transition-colors ${
                        isSelected
                          ? "border-(--brand-primary) bg-(--pastel-violet) text-(--brand-primary)"
                          : "border-dashed border-(--border-subtle) text-(--text-secondary) hover:border-(--brand-primary) hover:text-(--brand-primary)"
                      }`}
                    >
                      {isSelected && (
                        <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-(--radius-pill) bg-(--brand-primary) px-1 text-[10px] font-bold text-(--brand-primary-contrast)">
                          {order + 1}
                        </span>
                      )}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* (b) Time bucket */}
          <div className="flex flex-col gap-2">
            <SectionTitle title="Time bucket" />
            <Segmented
              value={config.bucket}
              options={BUCKETS}
              onChange={handleBucketChange}
            />
          </div>

          {/* (c) Group-by property picker, grouped by category */}
          <div className="flex flex-col gap-3">
            <SectionTitle title="Group by" hint="order sets the column order" />

            {config.groupBy.length > 0 && (
              <ol className="flex flex-col gap-2">
                {config.groupBy.map((key, index) => (
                  <li
                    key={key}
                    className="flex items-center gap-2.5 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-2) px-3 py-2"
                  >
                    <span className="flex flex-col text-(--text-muted)">
                      <button
                        type="button"
                        aria-label={`Move ${fieldLabel(key)} up`}
                        disabled={index === 0}
                        onClick={() => moveField(index, -1)}
                        className="leading-none disabled:opacity-30 hover:text-(--text-primary)"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        aria-label={`Move ${fieldLabel(key)} down`}
                        disabled={index === config.groupBy.length - 1}
                        onClick={() => moveField(index, 1)}
                        className="leading-none disabled:opacity-30 hover:text-(--text-primary)"
                      >
                        ▼
                      </button>
                    </span>
                    <GripVertical size={14} className="text-(--text-faint)" />
                    <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-(--radius-pill) bg-(--brand-primary) px-1.5 text-[11px] font-bold text-(--brand-primary-contrast)">
                      {index + 1}
                    </span>
                    <span className="flex-1 text-[13px] font-medium text-(--text-primary)">
                      {fieldLabel(key)}
                    </span>
                    <button
                      type="button"
                      aria-label={`Remove ${fieldLabel(key)}`}
                      onClick={() => removeField(key)}
                      className="flex h-6 w-6 items-center justify-center rounded-(--radius-md) text-(--text-muted) hover:bg-(--surface-1) hover:text-(--status-danger)"
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ol>
            )}

            <div className="flex flex-col gap-4">
              {categories.map((category) => {
                const Icon = category.icon;
                return (
                  <div key={category.id} className="flex flex-col gap-2">
                    <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-(--text-muted)">
                      <Icon size={13} strokeWidth={2} />
                      {category.label}
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {category.fields.map((key) => {
                        const isSelected = config.groupBy.includes(key);
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => toggleField(key)}
                            aria-pressed={isSelected}
                            className={`inline-flex items-center rounded-(--radius-pill) border px-3 py-1.5 text-[12px] font-medium transition-colors ${
                              isSelected
                                ? "border-(--brand-primary) bg-(--pastel-violet) text-(--brand-primary)"
                                : "border-dashed border-(--border-subtle) text-(--text-secondary) hover:border-(--brand-primary) hover:text-(--brand-primary)"
                            }`}
                          >
                            {category.fieldDescriptors[key]?.label ??
                              FIELD_LABELS[key]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-(--border-subtle) px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 items-center justify-center rounded-(--radius-md) border-0 bg-(--brand-primary) px-4 text-[13px] font-bold text-(--brand-primary-contrast) transition hover:bg-(--brand-primary-hover)"
          >
            Done
          </button>
        </div>
      </aside>
    </div>,
    document.body,
  );
};

export default ConfigureDrawer;
